import { styled } from '@mui/material/styles';
import { notEmpty } from '@shared/utils/array';
import type { ElementType } from 'react';
import FormatDate from '@/component/Date/FormatDate';
import FormatDuration from '@/component/Date/FormatDuration';
import Empty from '@/component/Empty';
import { PaneFixedHeader as Pane } from '@/component/Layout';
import { Markdown } from '@/component/Markdown';
import { Parameter, ParameterList } from '@/component/ParameterList';
import { useAlarmByIdQuery } from '@/feature/alarm/useAlarmQuery';
import { useTexts } from '@/feature/lang';
import { SelectedEquipmentContentFilter } from '@/view/fleet/components/EquipmentContentFilter';
import { useEquipmentTimeZone } from '@/view/fleet/lib/timezone';
import DetailsContextProvider from '@/view/fleet/pages/DetailsContextProvider';
import Severity from '../component/Severity';
import { useAlarmDecorator } from '../hooks/useAlarmDecorator';
import type { Alarm } from '../types';
import { ValueParameter } from './ValueParameter';
import { ViewSignalTrendButton } from './ViewSignalTrendButton';

const Actions = styled('div', { name: 'DetailsPane', slot: 'Actions' })(
  ({ theme }) => ({
    marginTop: theme.spacing(2),
  })
);

const useAlarm = (alarmId: string) => {
  const { data, isLoading } = useAlarmByIdQuery(alarmId);
  const alarm = useAlarmDecorator([data].filter(notEmpty)).at(0);

  return { alarm, isLoading };
};

export interface AlarmHeaderProps {
  alarm: Alarm | undefined;
  isLoading: boolean;
}

interface DetailsPaneProps {
  alarmId: string;
  HeaderContent: ElementType<AlarmHeaderProps>;
}

export const DetailsPane = ({ alarmId, HeaderContent }: DetailsPaneProps) => {
  const { alarm, isLoading } = useAlarm(alarmId);
  const texts = useTexts().fleet.alarms;
  const { timeZone } = useEquipmentTimeZone(alarm?.equipmentId);

  return (
    <DetailsContextProvider selected={[alarm?.equipmentId].filter(notEmpty)}>
      <Pane>
        <Pane.Header>
          <HeaderContent alarm={alarm} isLoading={isLoading} />
        </Pane.Header>
        <Pane.Content>
          {alarm || isLoading ? (
            <>
              <ParameterList isLoading={isLoading} fullWidth>
                <Parameter label={texts.model.severity}>
                  {alarm && (
                    <Severity
                      severity={alarm.severity}
                      label={alarm.severityLabel}
                    />
                  )}
                </Parameter>
                <Parameter label={texts.model.status}>
                  {alarm?.statusLabel}
                </Parameter>
                <Parameter label={texts.model.triggered}>
                  <FormatDate
                    date={alarm?.eventTriggered}
                    timeZone={timeZone}
                  />
                </Parameter>
                {alarm?.eventClosed && (
                  <>
                    <Parameter label={texts.model.closed}>
                      <FormatDate
                        date={alarm.eventClosed}
                        timeZone={timeZone}
                      />
                    </Parameter>
                    <Parameter label={texts.model.duration}>
                      <FormatDuration
                        from={new Date(alarm.eventTriggered)}
                        to={new Date(alarm.eventClosed)}
                      />
                    </Parameter>
                  </>
                )}
                <Parameter label={texts.model.name} verticalAlign="top">
                  {alarm?.name}
                </Parameter>
                <Parameter label={texts.model.description} verticalAlign="top">
                  <Markdown paper>{alarm?.description}</Markdown>
                </Parameter>
                <Parameter label={texts.model.consequences} verticalAlign="top">
                  <Markdown paper>{alarm?.consequences}</Markdown>
                </Parameter>
                <Parameter label={texts.model.actions} verticalAlign="top">
                  <Markdown paper>{alarm?.actions}</Markdown>
                </Parameter>
                <SelectedEquipmentContentFilter categories={['SCREEN']}>
                  <ValueParameter alarm={alarm} />
                </SelectedEquipmentContentFilter>
              </ParameterList>
              <SelectedEquipmentContentFilter categories={['SCREEN']}>
                <Actions>
                  <ViewSignalTrendButton alarm={alarm} />
                </Actions>
              </SelectedEquipmentContentFilter>
            </>
          ) : (
            <Empty text={texts.details.empty.text} />
          )}
        </Pane.Content>
      </Pane>
    </DetailsContextProvider>
  );
};
